import { HelpCircle, InfoCircle } from "@untitledui/icons";
import {
	type ComponentType,
	createContext,
	type HTMLAttributes,
	type ReactNode,
	type Ref,
	useContext,
} from "react";
import type {
	InputProps as AriaInputProps,
	TextFieldProps as AriaTextFieldProps,
} from "react-aria-components";
import {
	Group as AriaGroup,
	Input as AriaInput,
	TextField as AriaTextField,
} from "react-aria-components";
import { HintText } from "@/components/base/input/hint-text";
import { Label } from "@/components/base/input/label";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx, sortCx } from "@/utils/cx";

export interface InputBaseProps extends TextFieldProps {
	/** Tooltip message on hover. */
	tooltip?: string;
	/**
	 * Input size.
	 * @default "sm"
	 */
	size?: "sm" | "md";
	/** Placeholder text. */
	placeholder?: string;
	/** Class name for the icon. */
	iconClassName?: string;
	/** Class name for the input. */
	inputClassName?: string;
	/** Class name for the input wrapper. */
	wrapperClassName?: string;
	/** Class name for the tooltip. */
	tooltipClassName?: string;
	/** Keyboard shortcut to display. */
	shortcut?: string | boolean;
	ref?: Ref<HTMLInputElement>;
	groupRef?: Ref<HTMLDivElement>;
	/** Icon component to display on the left side of the input. */
	icon?: ComponentType<HTMLAttributes<HTMLOrSVGElement>>;
	/** Hide the trailing error icon (e.g. for date/time to avoid overlap with native picker icon). */
	hideTrailingErrorIcon?: boolean;
}

export const InputBase = ({
	ref,
	tooltip,
	shortcut,
	groupRef,
	size = "sm",
	isInvalid,
	isDisabled,
	icon: Icon,
	placeholder,
	wrapperClassName,
	tooltipClassName,
	inputClassName,
	iconClassName,
	hideTrailingErrorIcon = false,
	// Omit this prop to avoid invalid HTML attribute warning
	isRequired: _isRequired,
	...inputProps
}: Omit<InputBaseProps, "label" | "hint">) => {
	// Check if the input has a leading icon or tooltip
	const showErrorIcon = isInvalid && !hideTrailingErrorIcon;
	const hasTrailingIcon = tooltip || showErrorIcon;
	const hasLeadingIcon = Icon;

	// If the input is inside a `TextFieldContext`, use its context to simplify applying styles
	const context = useContext(TextFieldContext);

	const inputSize = context?.size || size;

	const sizes = sortCx({
		sm: {
			root: cx(
				"px-3 py-2",
				hasTrailingIcon && "pr-9",
				hasLeadingIcon && "pl-10"
			),
			iconLeading: "left-3",
			iconTrailing: "right-3",
			shortcut: "pr-2.5",
		},
		md: {
			root: cx(
				"px-3.5 py-2.5",
				hasTrailingIcon && "pr-9.5",
				hasLeadingIcon && "pl-10.5"
			),
			iconLeading: "left-3.5",
			iconTrailing: "right-3.5",
			shortcut: "pr-3",
		},
	});

	return (
		<AriaGroup
			{...{ isDisabled, isInvalid }}
			className={({ isFocusWithin, isDisabled, isInvalid }) =>
				cx(
					"relative flex w-full flex-row place-content-center place-items-center rounded-lg shadow-xs ring-1 ring-primary ring-inset transition-shadow duration-100 ease-linear",

					isFocusWithin && !isDisabled && "ring-2 ring-brand",

					// Disabled state styles
					isDisabled && "cursor-not-allowed bg-disabled_subtle ring-disabled",
					"group-disabled:cursor-not-allowed group-disabled:bg-disabled_subtle group-disabled:ring-disabled",

					// Invalid state styles
					isInvalid && "ring-error_subtle",
					"group-invalid:ring-error_subtle",

					// Invalid state with focus-within styles
					isInvalid && isFocusWithin && "ring-2 ring-error",
					isFocusWithin && "group-invalid:ring-2 group-invalid:ring-error",

					context?.wrapperClassName,
					wrapperClassName
				)
			}
			ref={groupRef}
			style={
				isDisabled ? undefined : { backgroundColor: "var(--input-bg, #ffffff)" }
			}
		>
			{/* Leading icon and Payment icon */}
			{Icon && (
				<Icon
					className={cx(
						"pointer-events-none absolute size-5 text-gray-400 dark:text-gray-600",
						isDisabled && "text-gray-400 dark:text-gray-500",
						sizes[inputSize].iconLeading,
						context?.iconClassName,
						iconClassName
					)}
				/>
			)}

			{/* Input field */}
			<AriaInput
				{...(inputProps as AriaInputProps)}
				className={cx(
					"m-0 w-full bg-transparent text-gray-900 text-md outline-hidden ring-0 placeholder:text-gray-500 autofill:rounded-lg autofill:text-gray-900 dark:text-gray-50 dark:autofill:text-gray-50 dark:placeholder:text-gray-400",
					isDisabled && "cursor-not-allowed text-gray-400 dark:text-gray-500",
					sizes[inputSize].root,
					context?.inputClassName,
					inputClassName
				)}
				placeholder={placeholder}
				ref={ref}
			/>

			{/* Tooltip and help icon */}
			{tooltip && !showErrorIcon && (
				<Tooltip placement="top" title={tooltip}>
					<TooltipTrigger
						className={cx(
							"absolute cursor-pointer text-gray-400 transition duration-200 hover:text-gray-500 focus:text-gray-500 dark:text-gray-600 dark:focus:text-gray-500 dark:hover:text-gray-500",
							sizes[inputSize].iconTrailing,
							context?.tooltipClassName,
							tooltipClassName
						)}
					>
						<HelpCircle className="size-4" />
					</TooltipTrigger>
				</Tooltip>
			)}

			{/* Invalid icon */}
			{showErrorIcon && (
				<InfoCircle
					className={cx(
						"pointer-events-none absolute size-4 text-fg-error-secondary",
						sizes[inputSize].iconTrailing,
						context?.tooltipClassName,
						tooltipClassName
					)}
				/>
			)}

			{/* Shortcut */}
			{shortcut && (
				<div
					className={cx(
						"pointer-events-none absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-40% to-white pl-8 dark:to-gray-950",
						sizes[inputSize].shortcut
					)}
				>
					<span
						aria-hidden="true"
						className={cx(
							"pointer-events-none select-none rounded px-1 py-px font-medium text-gray-500 text-xs ring-1 ring-gray-200 ring-inset dark:text-gray-400 dark:ring-gray-700",
							isDisabled && "bg-transparent text-gray-400 dark:text-gray-500"
						)}
					>
						{typeof shortcut === "string" ? shortcut : "⌘K"}
					</span>
				</div>
			)}
		</AriaGroup>
	);
};

InputBase.displayName = "InputBase";

interface BaseProps {
	/** Label text for the input */
	label?: string;
	/** Helper text displayed below the input */
	hint?: ReactNode;
}

interface TextFieldProps
	extends BaseProps,
		AriaTextFieldProps,
		Pick<
			InputBaseProps,
			| "size"
			| "wrapperClassName"
			| "inputClassName"
			| "iconClassName"
			| "tooltipClassName"
		> {
	ref?: Ref<HTMLDivElement>;
}

const TextFieldContext = createContext<TextFieldProps>({});

export const TextField = ({ className, ...props }: TextFieldProps) => (
	<TextFieldContext.Provider value={props}>
		<AriaTextField
			{...props}
			className={(state) =>
				cx(
					"group flex h-max w-full flex-col items-start justify-start gap-1.5",
					typeof className === "function" ? className(state) : className
				)
			}
			data-input-wrapper
		/>
	</TextFieldContext.Provider>
);

TextField.displayName = "TextField";

interface InputProps extends InputBaseProps, BaseProps {
	/** Whether to hide required indicator from label */
	hideRequiredIndicator?: boolean;
}

export const Input = ({
	size = "sm",
	placeholder,
	icon: Icon,
	label,
	hint,
	shortcut,
	hideRequiredIndicator,
	hideTrailingErrorIcon,
	className,
	ref,
	groupRef,
	tooltip,
	iconClassName,
	inputClassName,
	wrapperClassName,
	tooltipClassName,
	...props
}: InputProps) => (
	<TextField
		aria-label={label ? undefined : placeholder}
		{...props}
		className={className}
	>
		{({ isRequired, isInvalid }) => (
			<>
				{label && (
					<Label
						isRequired={
							hideRequiredIndicator ? !hideRequiredIndicator : isRequired
						}
					>
						{label}
					</Label>
				)}

				<InputBase
					{...{
						ref,
						groupRef,
						size,
						placeholder,
						icon: Icon,
						shortcut,
						iconClassName,
						inputClassName,
						wrapperClassName,
						tooltipClassName,
						tooltip,
						hideTrailingErrorIcon,
					}}
				/>

				{hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
			</>
		)}
	</TextField>
);

Input.displayName = "Input";
